import React, { useEffect, useState, useCallback } from 'react';
import { portalApi } from '../../lib/parceiroApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Pencil, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyImovel = {
  tipo: 'imovel', codigo_interno: '', proprietario: '', morador: '', telefone: '', whatsapp: '', email: '',
  condominio: '', bloco: '', unidade: '',
  endereco: { cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', zona: '' },
  observacoes: '', ativo: true,
};

export default function ParceiroImoveis() {
  const [imoveis, setImoveis] = useState(null);
  const [cidades, setCidades] = useState([]);
  const [show, setShow] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyImovel });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    portalApi.listImoveis().then((r) => setImoveis(r.data)).catch(() => setImoveis([]));
  }, []);
  useEffect(() => {
    load();
    portalApi.publicCidades().then((r) => setCidades(r.data)).catch(() => {});
  }, [load]);

  const abrir = (im = null) => {
    if (im) {
      setEditId(im.id);
      setForm({ ...emptyImovel, ...im, endereco: { ...emptyImovel.endereco, ...(im.endereco || {}) } });
    } else {
      setEditId(null);
      setForm({ ...emptyImovel });
    }
    setShow(true);
  };

  const salvar = async () => {
    setSaving(true);
    try {
      const { id, parceiro_id, created_at, ...payload } = form;
      if (editId) await portalApi.updateImovel(editId, payload);
      else await portalApi.createImovel(payload);
      toast.success(editId ? 'Imóvel atualizado' : 'Imóvel cadastrado');
      setShow(false);
      load();
    } catch (err) {
      toast.error('Erro ao salvar imóvel');
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (im) => {
    const { id, parceiro_id, created_at, ...payload } = im;
    await portalApi.updateImovel(im.id, { ...payload, endereco: im.endereco || {}, ativo: !im.ativo });
    toast.success(im.ativo ? 'Imóvel desativado' : 'Imóvel reativado');
    load();
  };

  const cidadeSel = cidades.find((c) => c.nome === form.endereco.cidade && c.estado === form.endereco.estado);
  const setEnd = (patch) => setForm((f) => ({ ...f, endereco: { ...f.endereco, ...patch } }));

  return (
    <div className="space-y-4" data-testid="parceiro-imoveis">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-[#3D4F5F]">Imóveis / Unidades</h1>
        <Button className="bg-[#2B7A3D] hover:bg-[#236632]" onClick={() => abrir()} data-testid="btn-cadastrar-imovel">
          <Plus size={16} className="mr-1" /> Cadastrar
        </Button>
      </div>

      {!imoveis ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" size={26} /></div>
      ) : imoveis.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">Nenhum imóvel cadastrado ainda.</div>
      ) : (
        <div className="space-y-2">
          {imoveis.map((im) => (
            <div key={im.id} className="bg-white rounded-xl border border-gray-200 p-3.5 flex items-start justify-between gap-2" data-testid={`imovel-${im.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-semibold text-sm text-[#3D4F5F]">
                  <Building2 size={15} className="shrink-0" />
                  <span className="truncate">
                    {im.condominio ? `${im.condominio} ${im.bloco ? '· ' + im.bloco : ''} ${im.unidade ? '· ' + im.unidade : ''}` : `${im.endereco?.rua || ''}, ${im.endereco?.numero || ''}`}
                  </span>
                  {im.codigo_interno && <span className="text-xs text-gray-400 shrink-0">({im.codigo_interno})</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 truncate">
                  {im.endereco?.bairro && `${im.endereco.bairro} · `}{im.endereco?.cidade}{im.endereco?.zona ? ` · ${im.endereco.zona}` : ''}
                  {im.morador && ` · Morador: ${im.morador}`}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => abrir(im)} data-testid={`editar-imovel-${im.id}`}><Pencil size={15} /></Button>
                <Button size="sm" variant="ghost" className="text-xs text-gray-500" onClick={() => toggleAtivo(im)}>{im.ativo ? 'Desativar' : 'Reativar'}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Editar imóvel' : 'Cadastrar imóvel'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {['imovel', 'condominio'].map((t) => (
                <button key={t} onClick={() => setForm((f) => ({ ...f, tipo: t }))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.tipo === t ? 'bg-[#2B7A3D] text-white border-[#2B7A3D]' : 'border-gray-300 text-gray-600'}`}>
                  {t === 'imovel' ? 'Imóvel' : 'Condomínio'}
                </button>
              ))}
            </div>
            {form.tipo === 'condominio' && (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-3 sm:col-span-1"><Label>Condomínio</Label><Input value={form.condominio} onChange={(e) => setForm((f) => ({ ...f, condominio: e.target.value }))} /></div>
                <div><Label>Bloco</Label><Input value={form.bloco} onChange={(e) => setForm((f) => ({ ...f, bloco: e.target.value }))} /></div>
                <div><Label>Apto/Unidade</Label><Input value={form.unidade} onChange={(e) => setForm((f) => ({ ...f, unidade: e.target.value }))} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Código interno</Label><Input value={form.codigo_interno} onChange={(e) => setForm((f) => ({ ...f, codigo_interno: e.target.value }))} data-testid="imovel-codigo" /></div>
              <div><Label>Proprietário</Label><Input value={form.proprietario} onChange={(e) => setForm((f) => ({ ...f, proprietario: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Morador/Inquilino</Label><Input value={form.morador} onChange={(e) => setForm((f) => ({ ...f, morador: e.target.value }))} /></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))} /></div>
              <div><Label>E-mail</Label><Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-3"><Label>Rua *</Label><Input value={form.endereco.rua} onChange={(e) => setEnd({ rua: e.target.value })} data-testid="imovel-rua" /></div>
              <div><Label>Nº</Label><Input value={form.endereco.numero} onChange={(e) => setEnd({ numero: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Complemento</Label><Input value={form.endereco.complemento} onChange={(e) => setEnd({ complemento: e.target.value })} /></div>
              <div><Label>Bairro</Label><Input value={form.endereco.bairro} onChange={(e) => setEnd({ bairro: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Cidade *</Label>
                <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={`${form.endereco.cidade}|${form.endereco.estado}`}
                  onChange={(e) => { const [cid, uf] = e.target.value.split('|'); setEnd({ cidade: cid || '', estado: uf || '', zona: '' }); }} data-testid="imovel-cidade">
                  <option value="|">Selecione...</option>
                  {cidades.map((c) => <option key={c.id} value={`${c.nome}|${c.estado}`}>{`${c.nome}/${c.estado}`}</option>)}
                </select>
              </div>
              {cidadeSel?.zonas?.length > 0 ? (
                <div>
                  <Label>Zona/Região</Label>
                  <select className="w-full h-10 border border-gray-300 rounded-md px-2 text-sm" value={form.endereco.zona} onChange={(e) => setEnd({ zona: e.target.value })}>
                    <option value="">Selecione...</option>
                    {cidadeSel.zonas.map((z) => <option key={z.nome} value={z.nome}>{z.nome}</option>)}
                  </select>
                </div>
              ) : (
                <div><Label>CEP</Label><Input value={form.endereco.cep} onChange={(e) => setEnd({ cep: e.target.value })} /></div>
              )}
            </div>
            <div><Label>Observações</Label><Textarea rows={2} value={form.observacoes} onChange={(e) => setForm((f) => ({ ...f, observacoes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button className="bg-[#2B7A3D] hover:bg-[#236632]" disabled={saving || !form.endereco.rua || !form.endereco.cidade} onClick={salvar} data-testid="salvar-imovel">
              {saving ? <Loader2 className="animate-spin" size={16} /> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
